import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Loader2, MessageSquareText, Video } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { cn } from '@/lib/utils';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { getMyInterviews, prepareFeedback } from './myInterviewsApi';

// Interviews that can no longer happen: nothing to join.
const CLOSED_STATUSES = new Set([
  'cancelled', 'canceled', 'declined', 'declined_by_candidate', 'declined_by_interviewer',
]);
// An interview stays under "Upcoming" until this long after it starts.
const STILL_UPCOMING_MS = 2 * 60 * 60 * 1000;

const STATUS_LABELS = {
  confirmed: 'Confirmed',
  pending_acceptance: 'Waiting for replies',
  accepted_by_candidate: 'Candidate accepted',
  accepted_by_interviewer: 'You accepted',
  completed: 'Completed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  declined: 'Declined',
};

const statusLabel = (status) =>
  STATUS_LABELS[status] ||
  String(status || 'Scheduled').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const whenLabel = (interview) => {
  if (interview.slot_label) return interview.slot_label;
  if (!interview.starts_at) return 'Time to be confirmed';
  const date = new Date(interview.starts_at);
  return Number.isNaN(date.getTime())
    ? 'Time to be confirmed'
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const isUpcoming = (interview) => {
  if (CLOSED_STATUSES.has(interview.status) || interview.status === 'completed') return false;
  if (!interview.starts_at) return true;
  const starts = new Date(interview.starts_at).getTime();
  return Number.isNaN(starts) || starts + STILL_UPCOMING_MS >= Date.now();
};

function InterviewRow({ interview, onJoin, onFeedback, preparing }) {
  const closed = CLOSED_STATUSES.has(interview.status);
  const canJoin = !closed && (interview.live_room_id || interview.meet_link);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {interview.candidate_name || 'Candidate'}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {interview.job_title || 'Interview'} · {whenLabel(interview)}
        </p>
      </div>
      <span
        className={cn(
          'rounded-full border px-2 py-0.5 text-xs',
          closed ? 'border-destructive/30 text-destructive' : 'border-border text-muted-foreground',
        )}
      >
        {statusLabel(interview.status)}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={!canJoin} onClick={() => onJoin(interview)}>
          <Video className="mr-1.5 h-3.5 w-3.5" />
          Join
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={closed || preparing}
          onClick={() => onFeedback(interview)}
        >
          {preparing ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <MessageSquareText className="mr-1.5 h-3.5 w-3.5" />
          )}
          Give feedback
        </Button>
      </div>
    </li>
  );
}

function InterviewGroup({ title, interviews, ...rowProps }) {
  if (!interviews.length) return null;
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <ul className="divide-y divide-border rounded-lg border bg-card">
        {interviews.map((interview) => (
          <InterviewRow
            key={interview.booking_id}
            interview={interview}
            preparing={rowProps.preparingId === interview.booking_id}
            onJoin={rowProps.onJoin}
            onFeedback={rowProps.onFeedback}
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * The Interviewer's home: every interview they are on, with a way into the room
 * and to the feedback form. Nothing about the candidate beyond their name.
 */
export default function MyInterviews() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [state, setState] = useState('loading');
  const [preparingId, setPreparingId] = useState('');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setInterviews(await getMyInterviews());
      setState('ready');
    } catch (err) {
      setState('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [upcoming, earlier] = useMemo(() => {
    const soon = interviews.filter(isUpcoming).reverse();
    return [soon, interviews.filter((interview) => !isUpcoming(interview))];
  }, [interviews]);

  const join = (interview) => {
    if (interview.live_room_id) {
      navigate(`/interview-room/${encodeURIComponent(interview.live_room_id)}`);
    } else if (interview.meet_link) {
      window.open(interview.meet_link, '_blank', 'noopener,noreferrer');
    }
  };

  const giveFeedback = async (interview) => {
    setPreparingId(interview.booking_id);
    try {
      const url = await prepareFeedback(interview.booking_id);
      if (!url) throw new Error('The feedback form is not ready yet. Try again in a minute.');
      window.location.assign(url);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Could not open the feedback form.');
      setPreparingId('');
    }
  };

  return (
    <EmployerLayout>
      <div className="mx-auto w-full max-w-4xl space-y-6 p-4 sm:p-6">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-foreground">My interviews</h1>
          <p className="text-sm text-muted-foreground">
            Interviews you are on. Join the room, or give feedback once it is over.
          </p>
        </header>

        {state === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your interviews…
          </div>
        )}

        {state === 'error' && (
          <div className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm">
            <span className="text-muted-foreground">Your interviews could not be loaded.</span>
            <Button size="sm" variant="outline" onClick={load}>
              Try again
            </Button>
          </div>
        )}

        {state === 'ready' && !interviews.length && (
          <div className="flex items-center gap-3 rounded-lg border px-4 py-6 text-sm text-muted-foreground">
            <CalendarClock className="h-5 w-5 shrink-0" />
            No interviews yet. When someone books you on an interview, it shows up here.
          </div>
        )}

        {state === 'ready' && (
          <>
            <InterviewGroup
              title="Upcoming"
              interviews={upcoming}
              preparingId={preparingId}
              onJoin={join}
              onFeedback={giveFeedback}
            />
            <InterviewGroup
              title="Earlier"
              interviews={earlier}
              preparingId={preparingId}
              onJoin={join}
              onFeedback={giveFeedback}
            />
          </>
        )}
      </div>
    </EmployerLayout>
  );
}
